/**
 * Namespace derivation for the DSL Explorer.
 *
 * The registry keys functions by dotted path (e.g. "Watchlist.Symbol.Price").
 * The Explorer groups them by their leading segment ("Watchlist"). A function
 * may opt into an explicit group via the optional `namespace` meta field;
 * otherwise the prefix before the first dot is used, and a path with no dot
 * (e.g. "Help") is its own group.
 */

import type { DslListedFunction } from "./types";

/** The group a single listed function belongs to. */
export function namespaceOf(fn: DslListedFunction): string {
  if (fn.namespace && fn.namespace.trim()) return fn.namespace.trim();
  const dot = fn.path.indexOf(".");
  return dot === -1 ? fn.path : fn.path.slice(0, dot);
}

export interface DslNamespaceGroup {
  name: string;
  fns: DslListedFunction[];
}

/**
 * Group a flat list of functions by namespace, preserving the (already
 * path-sorted) order within each group and sorting the groups by name.
 */
export function groupByNamespace(
  list: DslListedFunction[]
): DslNamespaceGroup[] {
  const groups = new Map<string, DslListedFunction[]>();
  for (const fn of list) {
    const ns = namespaceOf(fn);
    const bucket = groups.get(ns);
    if (bucket) bucket.push(fn);
    else groups.set(ns, [fn]);
  }
  return [...groups.entries()]
    .map(([name, fns]) => ({ name, fns }))
    .sort((a, b) => a.name.localeCompare(b.name));
}
